import { useEffect, useMemo, useState } from 'react';
import { emailService } from '../../services/emailService';
import { useSubscription } from '../../context/SubscriptionContext';

/** One email staff are about to cause: its type, the records, and what to call it ("offer"). */
export interface UpcomingRequest {
  type: string;
  ids: string[];
  noun: string;
}

/** What will go out for one request: nothing when the type is off; otherwise who's emailed and who has no address. */
export interface UpcomingGroup {
  type: string;
  noun: string;
  on: boolean;
  /** Records that have an address, and the addresses for all of them. */
  emailed: number;
  missing: number;
  to: string[];
}

/**
 * Before staff confirm an action that emails families, ask which emails go
 * out and to whom. Skipped entirely when the school's plan has no Email.
 */
export function useUpcomingEmails(requests: UpcomingRequest[]) {
  const { hasFeature } = useSubscription();
  const inPlan = hasFeature('email');
  const asked = useMemo(() => requests.filter(r => r.ids.length > 0), [requests]);
  const key = JSON.stringify(asked);
  const [state, setState] = useState<{ key: string; groups: UpcomingGroup[]; error: string | null } | null>(null);

  useEffect(() => {
    if (!inPlan || asked.length === 0) return undefined;
    let stale = false;
    Promise.all(asked.map(r => emailService.upcoming(r.type, r.ids).then((u): UpcomingGroup => {
      const lists = r.ids.map(id => u.recipients[id] ?? []);
      return {
        type: r.type,
        noun: r.noun,
        on: u.on,
        emailed: lists.filter(l => l.length > 0).length,
        missing: lists.filter(l => l.length === 0).length,
        to: Array.from(new Set(lists.flat())),
      };
    })))
      .then(groups => { if (!stale) setState({ key, groups, error: null }); })
      .catch(err => { if (!stale) setState({ key, groups: [], error: err?.message || 'Couldn’t check the emails' }); });
    return () => { stale = true; };
    // `key` captures `asked`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPlan, key]);

  const current = state?.key === key ? state : null;
  const groups = useMemo(() => (current?.groups ?? []).filter(g => g.on), [current]);
  return {
    loading: inPlan && asked.length > 0 && !current,
    error: current?.error ?? null,
    /** Only the emails that are switched on. */
    groups,
    /** At least one family will get an email. */
    sending: groups.some(g => g.emailed > 0),
  };
}

export type UpcomingEmails = ReturnType<typeof useUpcomingEmails>;
